import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Seo, faqPageJsonLd } from "@/components/Seo";
import { AccessibilityBar } from "@/components/AccessibilityBar";
import { LeadHoneypot } from "@/components/LeadHoneypot";
import { useLeadSubmit, CONTACT_EMAIL } from "@/hooks/useLeadSubmit";
import { trackCtaClick, trackEmailClick, trackFaqOpen, type CtaLocation } from "@/lib/tracking";
import { gtmEnabled, openConsentSettings } from "@/lib/consent";
import {
  INCLUDED,
  LANDING_FAQ,
  SERVICE_OPTIONS,
  STEPS,
  TIMELINE_OPTIONS,
  variantFor,
  type Variant,
} from "@/content/wcagAudit";
import logo from "@/assets/logo.svg";
import logoDark from "@/assets/logo-dark.svg";

export const FORM_ID = "wcag_audit_landing";

// useLayoutEffect warns during pre-rendering; on the server there is nothing
// to lay out, so fall back to useEffect there.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

type Fields = {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  timeline: string;
  scope: string;
};
type FieldKey = keyof Fields;
type Errors = Partial<Record<FieldKey, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(f: Fields): Errors {
  const e: Errors = {};
  if (!f.name.trim()) e.name = "Enter your full name";
  if (!f.email.trim()) e.email = "Enter your work email";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Enter an email address like name@company.com";
  return e;
}

/** Field ids, in visual order, so the error summary links match the form. */
const FIELD_IDS: Record<FieldKey, string> = {
  name: "wa-name",
  email: "wa-email",
  phone: "wa-phone",
  company: "wa-company",
  service: "wa-service",
  timeline: "wa-timeline",
  scope: "wa-scope",
};

const focusRing =
  "focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring";
const selectClass = `flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background md:text-sm ${focusRing}`;

/** Link to the form that records which CTA was used. */
function FormCta({
  location,
  children,
  className = "",
  size = "lg",
}: {
  location: CtaLocation;
  children: React.ReactNode;
  className?: string;
  size?: "lg" | "sm" | "default";
}) {
  return (
    <Button variant="hero" size={size} className={`min-h-[44px] ${className}`} asChild>
      <a href="#lead-form" onClick={() => trackCtaClick(location)}>
        {children}
      </a>
    </Button>
  );
}

function LeadForm({ variant }: { variant: Variant }) {
  const navigate = useNavigate();
  const { honeypotRef, loading, submitError, markStarted, submit } = useLeadSubmit(FORM_ID);
  const [fields, setFields] = useState<Fields>({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: variant.service,
    timeline: "",
    scope: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const summaryRef = useRef<HTMLDivElement>(null);
  const [showSummary, setShowSummary] = useState(false);
  // Bumped on every failed submit, so focus moves to the summary each time
  // (and never while the visitor is typing a correction).
  const [failedSubmits, setFailedSubmits] = useState(0);
  // Follow the headline variant until the visitor picks a service themselves.
  const serviceTouched = useRef(false);

  useEffect(() => {
    if (!serviceTouched.current) setFields((f) => ({ ...f, service: variant.service }));
  }, [variant.service]);

  // Move focus to the error summary once it is on screen (WCAG 3.3.1).
  useEffect(() => {
    if (failedSubmits) summaryRef.current?.focus();
  }, [failedSubmits]);

  const set =
    (key: FieldKey) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      markStarted(fields.service);
      if (key === "service") serviceTouched.current = true;
      setFields((f) => ({ ...f, [key]: e.target.value }));
      if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setShowSummary(true);
      setFailedSubmits((n) => n + 1);
      return;
    }
    setShowSummary(false);

    const service = SERVICE_OPTIONS.find((o) => o.value === fields.service);
    const result = await submit({
      name: fields.name,
      email: fields.email,
      phone: fields.phone,
      company: fields.company,
      service: fields.service,
      requirement: service?.label,
      timeline: fields.timeline,
      scope: fields.scope,
      page_variant: variant.key,
    });
    if (result.ok) navigate("/wcag-audit/thank-you");
  };

  const errorList = (Object.keys(FIELD_IDS) as FieldKey[]).filter((k) => errors[k]);
  const describedBy = (key: FieldKey, hint?: string) =>
    [hint, errors[key] ? `${FIELD_IDS[key]}-error` : undefined].filter(Boolean).join(" ") || undefined;
  const fieldError = (key: FieldKey) =>
    errors[key] ? (
      <p id={`${FIELD_IDS[key]}-error`} className="mt-1 text-sm text-destructive">
        <span className="sr-only">Error: </span>
        {errors[key]}
      </p>
    ) : null;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby="lead-form-heading"
      className="card-surface relative p-5 shadow-lg sm:p-6 md:p-8"
    >
      <h2 id="lead-form-heading" className="font-display text-2xl font-bold text-foreground">
        Get your audit quote
      </h2>
      <p className="mt-1 mb-5 text-base text-muted-foreground">
        We reply within 24 hours with scope, timeline and a fixed fee.
      </p>

      {showSummary && errorList.length > 0 && (
        <div
          ref={summaryRef}
          tabIndex={-1}
          aria-labelledby="wa-error-summary-heading"
          className={`mb-5 rounded-md border-2 border-destructive p-4 ${focusRing}`}
        >
          <h3 id="wa-error-summary-heading" className="font-display text-base font-bold text-foreground">
            There {errorList.length === 1 ? "is a problem" : `are ${errorList.length} problems`} with the form
          </h3>
          <ul className="mt-2 list-none space-y-1">
            {errorList.map((k) => (
              <li key={k}>
                <a
                  href={`#${FIELD_IDS[k]}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(FIELD_IDS[k])?.focus();
                  }}
                  className={`text-sm font-medium text-destructive underline underline-offset-4 rounded-sm ${focusRing}`}
                >
                  {errors[k]}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mb-4 text-sm text-muted-foreground">
        Fields marked with an asterisk (<span className="text-destructive">*</span>) are required.
      </p>

      <LeadHoneypot id="wa-ref-hp" ref={honeypotRef} />

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor={FIELD_IDS.name} className="mb-1 block text-sm font-medium text-foreground">
              Full name <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <Input
              id={FIELD_IDS.name}
              value={fields.name}
              onChange={set("name")}
              autoComplete="name"
              required
              aria-invalid={!!errors.name}
              aria-describedby={describedBy("name")}
            />
            {fieldError("name")}
          </div>
          <div>
            <Label htmlFor={FIELD_IDS.email} className="mb-1 block text-sm font-medium text-foreground">
              Work email <span className="text-destructive" aria-hidden="true">*</span>
            </Label>
            <Input
              id={FIELD_IDS.email}
              type="email"
              value={fields.email}
              onChange={set("email")}
              autoComplete="email"
              required
              aria-invalid={!!errors.email}
              aria-describedby={describedBy("email")}
            />
            {fieldError("email")}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor={FIELD_IDS.phone} className="mb-1 block text-sm font-medium text-foreground">
              Phone
            </Label>
            <Input
              id={FIELD_IDS.phone}
              type="tel"
              value={fields.phone}
              onChange={set("phone")}
              autoComplete="tel"
            />
          </div>
          <div>
            <Label htmlFor={FIELD_IDS.company} className="mb-1 block text-sm font-medium text-foreground">
              Company or organisation
            </Label>
            <Input
              id={FIELD_IDS.company}
              value={fields.company}
              onChange={set("company")}
              autoComplete="organization"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor={FIELD_IDS.service} className="mb-1 block text-sm font-medium text-foreground">
              What do you need?
            </Label>
            <select id={FIELD_IDS.service} value={fields.service} onChange={set("service")} className={selectClass}>
              {SERVICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor={FIELD_IDS.timeline} className="mb-1 block text-sm font-medium text-foreground">
              When do you need it?
            </Label>
            <select id={FIELD_IDS.timeline} value={fields.timeline} onChange={set("timeline")} className={selectClass}>
              <option value="">Select a timeline</option>
              {TIMELINE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor={FIELD_IDS.scope} className="mb-1 block text-sm font-medium text-foreground">
            What should we test?
          </Label>
          <p id="wa-scope-hint" className="mb-1 text-sm text-muted-foreground">
            Website addresses, apps or documents, and anything we should know.
          </p>
          <Textarea
            id={FIELD_IDS.scope}
            value={fields.scope}
            onChange={set("scope")}
            rows={3}
            maxLength={2000}
            aria-describedby={describedBy("scope", "wa-scope-hint")}
          />
        </div>

        <div>
          <Button variant="hero" size="lg" type="submit" className="min-h-[44px] w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
              </>
            ) : (
              "Get my audit quote"
            )}
          </Button>
          {/* Always mounted so the message is announced when it appears. */}
          <div role="alert" className="mt-2 text-sm text-destructive">
            {submitError}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            We use your details only to reply to this enquiry.{" "}
            <Link to="/privacy" className={`font-medium text-primary underline underline-offset-4 rounded-sm ${focusRing}`}>
              Privacy policy
            </Link>
          </p>
        </div>
      </div>
    </form>
  );
}

/**
 * Paid-traffic landing page for Google Ads. A focused page with its own minimal
 * header and footer (no site navigation to leak clicks), one form, and a
 * headline matched to the ad group via `?service=` (see content/wcagAudit.ts).
 *
 * Pre-rendering produces the default variant; the `?service=` variant is
 * applied in a layout effect, before the browser paints, so hydration matches
 * the static HTML and the visitor never sees the default headline flash.
 */
const WcagAuditPage = () => {
  const { search } = useLocation();
  const [variant, setVariant] = useState<Variant>(() => variantFor(null));
  useIsomorphicLayoutEffect(() => {
    setVariant(variantFor(new URLSearchParams(search).get("service")));
  }, [search]);

  // Mobile sticky CTA: shown once the hero has scrolled away, hidden again
  // while the form itself is on screen.
  const [heroVisible, setHeroVisible] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const hero = document.getElementById("hero-copy");
    const form = document.getElementById("lead-form");
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === hero) setHeroVisible(e.isIntersecting);
        if (e.target === form) setFormVisible(e.isIntersecting);
      }
    });
    if (hero) io.observe(hero);
    if (form) io.observe(form);
    return () => io.disconnect();
  }, []);
  const showSticky = !heroVisible && !formVisible;

  // Keep a focused element from being scrolled under the sticky bar (WCAG
  // 2.4.11). Only matters on mobile, where the bar is shown.
  const stickyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = document.documentElement;
    const h = showSticky ? stickyRef.current?.offsetHeight ?? 0 : 0;
    root.style.scrollPaddingBottom = h ? `${h}px` : "";
    return () => {
      root.style.scrollPaddingBottom = "";
    };
  }, [showSticky]);

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={variant.title}
        description={variant.description}
        path="/wcag-audit"
        jsonLd={faqPageJsonLd(LANDING_FAQ)}
      />

      <header className="border-b bg-background">
        {/* Text-size and display tools at every width (this page has no menu
            to hold them on mobile, unlike the main site header). */}
        <div className="border-b bg-muted/50">
          <div className="mx-auto flex min-h-11 max-w-7xl items-center justify-end px-4 py-1 sm:px-6 md:px-8 lg:px-16">
            <AccessibilityBar className="flex-wrap justify-end" />
          </div>
        </div>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 md:px-8 lg:px-16">
          <Link to="/" className={`flex items-center rounded-sm ${focusRing}`} aria-label="AccessBharat — Home">
            <img src={logo} alt="" width={320} height={72} className="h-10 w-auto sm:h-12 dark:hidden" aria-hidden="true" />
            <img src={logoDark} alt="" width={320} height={72} className="hidden h-10 w-auto sm:h-12 dark:block" aria-hidden="true" />
          </Link>
          <FormCta location="header" size="sm" className="hidden sm:inline-flex">
            Get a quote
          </FormCta>
        </div>
      </header>

      <main id="main-content" tabIndex={-1}>
        {/* Hero + form */}
        <section className="px-4 pb-12 pt-10 sm:px-6 md:px-8 md:pt-14 lg:px-16" aria-labelledby="wa-heading">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-12 lg:gap-12">
            <div id="hero-copy" className="min-w-0 lg:col-span-6 lg:pt-4">
              <p className="eyebrow mb-5">{variant.eyebrow}</p>
              <h1
                id="wa-heading"
                className="mb-5 font-display text-3xl font-extrabold leading-tight text-foreground sm:text-4xl lg:text-5xl"
              >
                {variant.h1}
              </h1>
              <p className="mb-8 max-w-xl text-lg leading-relaxed text-muted-foreground">{variant.intro}</p>
              <ul className="mb-8 list-none space-y-3">
                {[
                  "IAAP CPACC-certified auditors",
                  "Fixed fee agreed before we start",
                  "Every issue mapped to its WCAG criterion and fix",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-base text-foreground">
                    <Check size={20} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <FormCta location="hero" className="lg:hidden">
                Get my audit quote
              </FormCta>
            </div>
            <div id="lead-form" tabIndex={-1} className="min-w-0 scroll-mt-4 lg:col-span-6 focus:outline-none">
              <LeadForm variant={variant} />
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="section-padding section-alt" aria-labelledby="wa-included-heading">
          <div className="mx-auto max-w-7xl">
            <h2 id="wa-included-heading" className="mb-8 font-display text-2xl font-bold text-foreground md:text-3xl">
              What you get
            </h2>
            <ul className="grid list-none gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {INCLUDED.map((item) => (
                <li key={item.title} className="border-t-2 border-foreground pt-4">
                  <h3 className="font-display text-lg font-bold text-foreground">{item.title}</h3>
                  <p className="mt-1 text-base leading-relaxed text-muted-foreground">{item.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* How it works */}
        <section className="section-padding" aria-labelledby="wa-steps-heading">
          <div className="mx-auto max-w-7xl">
            <h2 id="wa-steps-heading" className="mb-8 font-display text-2xl font-bold text-foreground md:text-3xl">
              How it works
            </h2>
            <ol className="grid list-none gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <li key={s.name}>
                  <span className="font-mono text-sm font-bold text-primary" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-lg font-bold text-foreground">{s.name}</h3>
                  <p className="mt-1 text-base leading-relaxed text-muted-foreground">{s.detail}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* FAQ — native <details> so answers are in the pre-rendered HTML. */}
        <section className="section-padding section-alt" aria-labelledby="wa-faq-heading">
          <div className="mx-auto max-w-3xl">
            <h2 id="wa-faq-heading" className="mb-6 font-display text-2xl font-bold text-foreground md:text-3xl">
              Questions
            </h2>
            <div className="divide-y divide-border border-y border-border">
              {LANDING_FAQ.map((item) => (
                <details
                  key={item.question}
                  className="group py-1"
                  onToggle={(e) => {
                    if (e.currentTarget.open) trackFaqOpen(item.question);
                  }}
                >
                  <summary
                    className={`flex cursor-pointer list-none items-center justify-between gap-6 rounded-sm py-4 text-left font-display text-lg font-semibold text-foreground [&::-webkit-details-marker]:hidden ${focusRing}`}
                  >
                    <span>{item.question}</span>
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-input text-xl leading-none text-primary transition-transform group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <div className="pb-5 text-base leading-relaxed text-muted-foreground md:pr-14">{item.answer}</div>
                </details>
              ))}
            </div>
            <div className="mt-10">
              <FormCta location="footer">Get my audit quote</FormCta>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-footer px-4 pb-28 pt-10 text-footer-foreground/80 sm:px-6 md:px-8 md:pb-10 lg:px-16">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 text-sm md:flex-row md:items-center md:justify-between">
          <p className="flex items-center gap-2">
            <Mail size={16} className="shrink-0" aria-hidden="true" />
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              onClick={trackEmailClick}
              className="inline-flex min-h-[24px] items-center rounded-sm py-1 underline underline-offset-4 hover:text-footer-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-footer-foreground"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
          <ul className="flex list-none flex-wrap gap-x-6 gap-y-2">
            <li>
              <Link
                to="/privacy"
                className="inline-flex min-h-[24px] items-center rounded-sm py-1 hover:text-footer-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-footer-foreground"
              >
                Privacy policy
              </Link>
            </li>
            {gtmEnabled() && (
              <li>
                <button
                  type="button"
                  onClick={openConsentSettings}
                  className="inline-flex min-h-[24px] items-center rounded-sm py-1 hover:text-footer-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-footer-foreground"
                >
                  Cookie settings
                </button>
              </li>
            )}
            <li>© {new Date().getFullYear()} AccessBharat</li>
          </ul>
        </div>
      </footer>

      {/* Mobile sticky CTA. After the footer in the DOM so it comes last in the
          tab order; hidden (not just invisible) when it isn't needed. */}
      <div
        ref={stickyRef}
        hidden={!showSticky}
        className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 p-3 backdrop-blur md:hidden"
      >
        <FormCta location="sticky" className="w-full">
          Get my audit quote
        </FormCta>
      </div>
    </div>
  );
};

export default WcagAuditPage;
